import { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useCart } from '../../context/CartContext';
import { useFlag } from '../../context/FeatureFlagsContext';
import { formatOrderStatus } from '../../utils/orderStatus';
import { Navbar } from '../../components/Navbar';
import { BackButton } from '../../components/BackButton';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowLeft, 
  Package, 
  Clock,
  CheckCircle,
  XCircle,
  Truck,
  Filter,
  RotateCcw
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { FLOWER_SIZES } from '../../utils/flowerSizes';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export const ACTIVE_STATUSES = ['pending', 'confirmed', 'preparing', 'out_for_delivery'];
export const HISTORY_STATUSES = ['delivered', 'cancelled'];

// Shared by OrdersPage (Code) and OrdersPageModern.
export function useCustomerOrders() {
  const [statusFilter, setStatusFilter] = useState('active');
  const reorderEnabled = useFlag('customer.reorder', true);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  // Feb 2026 — React Query (Phase 2). Back-nav from an order detail now paints
  // instantly from cache; the background refetch (stale-while-revalidate) picks
  // up any new order status without flashing a spinner.
  const ordersQuery = useQuery({
    queryKey: ['customer', 'orders'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/api/orders`, { withCredentials: true });
      return data || [];
    },
  });
  const productsQuery = useQuery({
    queryKey: ['customer', 'products'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/api/products`);
      return data || [];
    },
  });
  const orders = ordersQuery.data || [];
  const products = productsQuery.data || [];
  const loading = ordersQuery.isLoading || productsQuery.isLoading;

  const reorderFromPastOrder = useCallback((order) => {
    const productsById = Object.fromEntries(products.map(p => [p.id, p]));
    let added = 0;
    let skipped = 0;
    (order.items || []).forEach(item => {
      const p = productsById[item.product_id];
      if (!p || p.stock <= 0) { skipped += 1; return; }
      const qty = Math.min(item.quantity, p.stock);
      if (qty <= 0) { skipped += 1; return; }
      // Preserve size for flower line items so the cart line matches the past order.
      if (item.size) {
        const cfg = FLOWER_SIZES.find((s) => s.key === item.size);
        if (cfg) {
          addToCart(p, qty, { size: cfg.key, eighths: cfg.eighths, unitPrice: cfg.price });
          added += 1;
          return;
        }
      }
      addToCart(p, qty);
      added += 1;
    });
    if (added === 0) {
      toast.error('None of those items are available right now');
      return;
    }
    if (skipped > 0) {
      toast.warning(`Added ${added} item${added > 1 ? 's' : ''} to cart; ${skipped} unavailable`);
    } else {
      toast.success(`Added ${added} item${added > 1 ? 's' : ''} to cart`);
    }
    navigate('/customer/cart');
  }, [products, addToCart, navigate]);

  // Phase 5.8 — default to ACTIVE orders so the page acts like a live tracker.
  // Delivered/cancelled are kept available under HISTORY for receipts + reorders.
  const filteredOrders = (() => {
    if (statusFilter === 'active')  return orders.filter(o => ACTIVE_STATUSES.includes(o.status));
    if (statusFilter === 'history') return orders.filter(o => HISTORY_STATUSES.includes(o.status));
    if (statusFilter === 'all')     return orders;
    return orders.filter(o => o.status === statusFilter);
  })();
  const activeCount  = orders.filter(o => ACTIVE_STATUSES.includes(o.status)).length;
  const historyCount = orders.filter(o => HISTORY_STATUSES.includes(o.status)).length;

  return {
    statusFilter, setStatusFilter, reorderEnabled, orders, loading,
    reorderFromPastOrder, filteredOrders, activeCount, historyCount,
  };
}

export function OrdersPage() {
  const {
    statusFilter, setStatusFilter, reorderEnabled, orders, loading,
    reorderFromPastOrder, filteredOrders, activeCount, historyCount,
  } = useCustomerOrders();

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-[rgba(255,159,10,0.1)] text-[#FF9F0A] border-[#FF9F0A]',
      confirmed: 'bg-[rgba(0,240,255,0.1)] text-[#00F0FF] border-[#00F0FF]',
      preparing: 'bg-[rgba(0,240,255,0.1)] text-[#00F0FF] border-[#00F0FF]',
      out_for_delivery: 'bg-[rgba(57,255,20,0.1)] text-[#39FF14] border-[#39FF14]',
      delivered: 'bg-[rgba(57,255,20,0.1)] text-[#39FF14] border-[#39FF14]',
      cancelled: 'bg-[rgba(255,59,48,0.1)] text-[#FF3B30] border-[#FF3B30]'
    };
    return styles[status] || styles.pending;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'confirmed':
      case 'preparing': return <Package className="w-4 h-4" />;
      case 'out_for_delivery': return <Truck className="w-4 h-4" />;
      case 'delivered': return <CheckCircle className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      default: return <Package className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050003]">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-[#39FF14] font-mono">
            <span className="cursor-blink">█</span> LOADING_ORDERS...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050003]" data-testid="orders-page">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <BackButton to="/customer" />
          <h1 className="text-[#39FF14] font-mono text-2xl font-bold neon-text">
            {'>'} MY_ORDERS
          </h1>
        </div>

        {/* Segmented control — ACTIVE / HISTORY / ALL */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex gap-1 bg-[#0C0612] border border-[#2A0F3D] p-1" data-testid="orders-segmented">
            {[
              { key: 'active',  label: 'ACTIVE',  count: activeCount  },
              { key: 'history', label: 'HISTORY', count: historyCount },
              { key: 'all',     label: 'ALL',     count: orders.length },
            ].map((t) => {
              const active = statusFilter === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setStatusFilter(t.key)}
                  data-testid={`orders-tab-${t.key}`}
                  className={`px-3 sm:px-4 py-2 font-mono text-xs uppercase tracking-wider transition-colors ${
                    active
                      ? 'bg-[#39FF14] text-[#050003]'
                      : 'text-[#A3C7A8] hover:text-[#39FF14]'
                  }`}
                >
                  {t.label} <span className={active ? 'text-[#050003]/70' : 'text-[#4B6B52]'}>({t.count})</span>
                </button>
              );
            })}
          </div>

          {/* Secondary fine-grain filter (only visible inside ACTIVE/HISTORY) */}
          {statusFilter !== 'all' && (
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-[#4B6B52]" />
              <Select
                value={statusFilter === 'active' || statusFilter === 'history' ? statusFilter : statusFilter}
                onValueChange={setStatusFilter}
              >
                <SelectTrigger className="w-44 bg-[#0C0612] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none" data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0C0612] border-[#2A0F3D] rounded-none">
                  <SelectItem value="active"           className="text-[#39FF14] font-mono focus:bg-[#2A0F3D]">ALL ACTIVE</SelectItem>
                  <SelectItem value="pending"          className="text-[#39FF14] font-mono focus:bg-[#2A0F3D]">— PENDING</SelectItem>
                  <SelectItem value="confirmed"        className="text-[#39FF14] font-mono focus:bg-[#2A0F3D]">— CONFIRMED</SelectItem>
                  <SelectItem value="out_for_delivery" className="text-[#39FF14] font-mono focus:bg-[#2A0F3D]">— OUT FOR DELIVERY</SelectItem>
                  <SelectItem value="history"          className="text-[#39FF14] font-mono focus:bg-[#2A0F3D]">ALL HISTORY</SelectItem>
                  <SelectItem value="delivered"        className="text-[#39FF14] font-mono focus:bg-[#2A0F3D]">— DELIVERED</SelectItem>
                  <SelectItem value="cancelled"        className="text-[#39FF14] font-mono focus:bg-[#2A0F3D]">— CANCELLED</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Orders List */}
        <div className="space-y-4">
          {filteredOrders.map(order => (
            <div 
              key={order.id} 
              className="bg-[#0C0612] border border-[#2A0F3D] p-6 hover:border-[#39FF14] transition-colors"
              data-testid={`order-row-${order.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-3">
                    {getStatusIcon(order.status)}
                    <h3 className="text-[#39FF14] font-mono font-bold">
                      ORDER #{order.id.slice(0, 8).toUpperCase()}
                    </h3>
                  </div>
                  <p className="text-[#4B6B52] font-mono text-sm mt-1">
                    {new Date(order.created_at).toLocaleString()}
                  </p>
                </div>
                <Badge className={`${getStatusBadge(order.status)} border rounded-none font-mono text-xs uppercase`}>
                  {formatOrderStatus(order.status)}
                </Badge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <p className="text-[#6B8E73] font-mono text-xs uppercase">Items</p>
                  <p className="text-[#A3C7A8] font-mono text-sm">
                    {(order.items || []).length} item{(order.items || []).length === 1 ? '' : 's'}
                  </p>
                </div>
                <div>
                  <p className="text-[#6B8E73] font-mono text-xs uppercase">Total</p>
                  <p className="text-[#39FF14] font-mono font-bold">${Number(order.total || 0).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-[#6B8E73] font-mono text-xs uppercase">Payment</p>
                  <p className="text-[#A3C7A8] font-mono text-sm uppercase">{order.payment_method}</p>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-[#2A0F3D] flex-wrap gap-2">
                <p className="text-[#4B6B52] font-mono text-xs truncate max-w-xs">
                  {order.delivery_address}
                </p>
                <div className="flex items-center gap-2">
                  {reorderEnabled && ['delivered', 'cancelled'].includes(order.status) && (
                    <Button
                      size="sm"
                      onClick={() => reorderFromPastOrder(order)}
                      className="bg-[#39FF14] text-[#050003] hover:bg-[#4AF626] rounded-none font-bold"
                      data-testid={`reorder-${order.id}`}
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      REORDER
                    </Button>
                  )}
                  <Link to={`/customer/orders/${order.id}`}>
                    <Button 
                      variant="outline" 
                      className="border-[#39FF14] text-[#39FF14] hover:bg-[rgba(57,255,20,0.1)] rounded-none"
                      data-testid={`view-order-${order.id}`}
                    >
                      {order.status === 'out_for_delivery' ? 'TRACK ORDER' : 'VIEW DETAILS'}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          ))}

          {filteredOrders.length === 0 && (
            <div className="text-center py-12" data-testid="orders-empty">
              <Package className="w-12 h-12 text-[#4B6B52] mx-auto mb-4" />
              {statusFilter === 'active' && (
                <>
                  <p className="text-[#A3C7A8] font-mono text-sm">// NO ACTIVE ORDERS</p>
                  <p className="text-[#4B6B52] font-mono text-xs mt-1">// You'll see live tracking here once you place one.</p>
                  <Link to="/customer">
                    <Button className="mt-6 bg-[#39FF14] text-[#050003] font-bold hover:bg-[#4AF626] rounded-none font-mono" data-testid="orders-empty-shop">
                      SHOP NOW →
                    </Button>
                  </Link>
                </>
              )}
              {statusFilter === 'history' && (
                <p className="text-[#4B6B52] font-mono">// NO COMPLETED ORDERS YET</p>
              )}
              {statusFilter !== 'active' && statusFilter !== 'history' && (
                <>
                  <p className="text-[#4B6B52] font-mono">// NO ORDERS FOUND</p>
                  <Button
                    variant="outline"
                    onClick={() => setStatusFilter('active')}
                    className="mt-4 border-[#2A0F3D] text-[#39FF14] hover:border-[#39FF14] rounded-none"
                  >
                    BACK TO ACTIVE
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
